(() => {
  if (window.__WN_REVIEW_DETAIL_V6__) return;
  window.__WN_REVIEW_DETAIL_V6__ = true;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const blockingInteractive = 'button,a,input,textarea,select,label';

  class ReviewDetail {
    constructor(root) {
      if (!root || root.dataset.reviewDetailReady === 'true') return;
      this.root = root;
      this.modal = root.querySelector('[data-review-modal]');
      this.content = this.modal?.querySelector('[data-review-modal-content]');
      this.viewer = this.modal?.querySelector('[data-review-image-viewer]');
      this.viewerImg = this.viewer?.querySelector('[data-review-image-viewer-img]');
      this.viewerCounter = this.viewer?.querySelector('[data-review-image-counter]');
      if (!this.modal || !this.content) return;
      root.dataset.reviewDetailReady = 'true';
      this.lastTrigger = null;
      this.images = [];
      this.imageIndex = 0;
      this.touchStartX = null;
      this.bind();
    }

    bind() {
      this.root.addEventListener('click', (event) => this.onRootClick(event));
      this.root.addEventListener('keydown', (event) => this.onRootKeydown(event));
      this.modal.addEventListener('click', (event) => this.onModalClick(event));
      document.addEventListener('keydown', (event) => this.onDocumentKeydown(event));
      this.viewer?.addEventListener('touchstart', (event) => {
        this.touchStartX = event.touches?.[0]?.clientX ?? null;
      }, { passive: true });
      this.viewer?.addEventListener('touchend', (event) => {
        if (this.touchStartX == null) return;
        const end = event.changedTouches?.[0]?.clientX ?? this.touchStartX;
        const dx = end - this.touchStartX;
        this.touchStartX = null;
        if (Math.abs(dx) > 42) this.showImage(this.imageIndex + (dx < 0 ? 1 : -1));
      }, { passive: true });
    }

    onRootClick(event) {
      if (performance.now() < (this.root.__wnSuppressReviewClickUntil || 0)) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      const explicit = event.target.closest('[data-review-open-trigger]');
      if (explicit && this.root.contains(explicit)) {
        const card = explicit.closest('[data-review-open]');
        if (!card) return;
        event.preventDefault();
        event.stopPropagation();
        this.open(card, explicit);
        return;
      }
      const card = event.target.closest('[data-review-open]');
      if (!card || !this.root.contains(card)) return;
      if (event.target.closest(blockingInteractive)) return;
      event.preventDefault();
      event.stopPropagation();
      this.open(card, card);
    }

    onRootKeydown(event) {
      const card = event.target.closest('[data-review-open]');
      if (!card || !this.root.contains(card)) return;
      if (event.key !== 'Enter' && event.key !== ' ') return;
      if (event.target.closest(blockingInteractive) && !event.target.matches('[data-review-open-trigger]')) return;
      event.preventDefault();
      this.open(card, event.target);
    }

    onModalClick(event) {
      const viewerClose = event.target.closest('[data-review-image-viewer-close]');
      if (viewerClose) { event.preventDefault(); this.closeViewer(); return; }
      const viewerPrev = event.target.closest('[data-review-image-prev]');
      if (viewerPrev) { event.preventDefault(); this.showImage(this.imageIndex - 1); return; }
      const viewerNext = event.target.closest('[data-review-image-next]');
      if (viewerNext) { event.preventDefault(); this.showImage(this.imageIndex + 1); return; }
      const modalImage = event.target.closest('[data-review-image]');
      if (modalImage) { event.preventDefault(); this.openViewer(Number(modalImage.dataset.reviewImageIndex) || 0); return; }
      const modalClose = event.target.closest('[data-review-modal-close]');
      if (modalClose) { event.preventDefault(); this.close(); }
    }

    onDocumentKeydown(event) {
      if (event.key !== 'Escape') return;
      if (this.viewer && !this.viewer.hidden) this.closeViewer();
      else if (!this.modal.hidden) this.close();
    }

    ensurePortal() {
      if (this.modal.parentElement !== document.body) document.body.appendChild(this.modal);
    }

    open(card, trigger) {
      const template = card.querySelector('[data-review-detail]');
      if (!template) return;
      this.ensurePortal();
      this.lastTrigger = trigger || card;
      this.content.replaceChildren(template.content.cloneNode(true));
      this.images = [...this.content.querySelectorAll('[data-review-image]')]
        .map((button) => button.dataset.reviewImageSrc || button.querySelector('img')?.src)
        .filter(Boolean);
      this.modal.hidden = false;
      this.modal.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('wn-review-modal-open');
      document.body.classList.add('wn-review-modal-open');
      requestAnimationFrame(() => {
        this.modal.classList.add('is-open');
        this.modal.querySelector('[data-review-modal-close]')?.focus({ preventScroll: true });
      });
    }

    close() {
      if (this.modal.hidden) return;
      this.closeViewer();
      this.modal.classList.remove('is-open');
      this.modal.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('wn-review-modal-open');
      document.body.classList.remove('wn-review-modal-open');
      window.setTimeout(() => {
        this.modal.hidden = true;
        this.content.replaceChildren();
        if (this.lastTrigger?.isConnected) this.lastTrigger.focus({ preventScroll: true });
        this.lastTrigger = null;
        this.images = [];
      }, reducedMotion.matches ? 0 : 180);
    }

    openViewer(index) {
      if (!this.viewer || !this.images.length) return;
      this.viewer.hidden = false;
      this.viewer.setAttribute('aria-hidden', 'false');
      this.showImage(index);
      requestAnimationFrame(() => this.viewer.classList.add('is-open'));
      this.viewer.querySelector('[data-review-image-viewer-close]')?.focus({ preventScroll: true });
    }

    closeViewer() {
      if (!this.viewer || this.viewer.hidden) return;
      this.viewer.classList.remove('is-open');
      this.viewer.setAttribute('aria-hidden', 'true');
      window.setTimeout(() => { if (this.viewer) this.viewer.hidden = true; }, reducedMotion.matches ? 0 : 150);
    }

    showImage(index) {
      if (!this.images.length || !this.viewerImg) return;
      this.imageIndex = ((index % this.images.length) + this.images.length) % this.images.length;
      this.viewerImg.src = this.images[this.imageIndex];
      if (this.viewerCounter) this.viewerCounter.textContent = `${this.imageIndex + 1} / ${this.images.length}`;
      const showNav = this.images.length > 1;
      this.viewer?.querySelectorAll('[data-review-image-prev],[data-review-image-next]').forEach((button) => { button.hidden = !showNav; });
    }
  }

  const boot = (scope = document) => scope.querySelectorAll?.('[data-review-surface]').forEach((root) => new ReviewDetail(root));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot(), { once: true }); else boot();
  document.addEventListener('shopify:section:load', (event) => boot(event.target));
})();
